import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import ReactDOMServer from 'react-dom/server';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {ActivityIcon} from 'shells/icon/svg/activity-icon';
// @ts-expect-error - imported from .js/.jsx file
import {MeetingIcon} from 'shells/icon/svg/meeting-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PhoneCallIcon} from 'shells/icon/svg/phone-call-icon';
// @ts-expect-error - imported from .js/.jsx file
import {VirtualMeetingIcon} from 'shells/icon/svg/virtual-meeting-icon';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {Divider} from 'shells/layout/divider';
import {Link} from 'shells/link/link';
import type {StatusBadgeProps} from 'shells/status-badge/status-badge';
import {Body} from 'shells/typography/body';

import type {
    NextStepsFragment_slackMessage,
    NextStepsFragment_nutshellNotification,
    MeetingStubFragment,
    MeetingFragment as Meeting,
    MeetingForLandingPage,
    GetSessionUser_loggedInUser as User,
    GetCalendars_calendars as ConflictCalendar,
    GetAssignments_meeting_assignments_edges_meetings as ManagedMeeting,
} from 'nutshell-graphql-types';

import MEETING_FRAGMENT from './graphql/fragments/meeting-fragment.graphql';

// Temporary ridiculous limit until we get to pagination
export const DEFAULT_SCHEDULER_LIST_LIMIT = 100;
export const SCHEDULER_VIEW_SEARCH = 'SCHEDULER_VIEW_SEARCH';
export const SCHEDULER_MANAGED_VIEW_SEARCH = 'SCHEDULER_MANAGED_VIEW_SEARCH';

export function nextStepsCacheUpdate({
    client,
    meetingId,
    nextStepsKey,
    nextStep,
}: {
    client: any;
    meetingId: string;
    nextStepsKey: 'slackMessage' | 'nutshellNotification';
    nextStep:
        | NextStepsFragment_slackMessage
        | NextStepsFragment_nutshellNotification
        | null
        | undefined;
}) {
    const meetingFragment = client.readFragment({
        id: meetingId,
        fragmentName: 'MeetingFragment',
        fragment: MEETING_FRAGMENT,
    });

    if (meetingFragment) {
        client.writeFragment({
            id: meetingId,
            fragment: MEETING_FRAGMENT,
            fragmentName: 'MeetingFragment',
            data: {
                ...meetingFragment,
                form: {
                    ...meetingFragment.form,
                    nextSteps: {
                        ...meetingFragment.form.nextSteps,
                        [nextStepsKey]: nextStep,
                    },
                },
            },
        });
    }
}

export function getActivityIcon(activityType: string): React.ComponentType<any> {
    switch (activityType) {
        case 'Meeting':
            return MeetingIcon;
        case 'Virtual Meeting':
            return VirtualMeetingIcon;
        case 'Phone Call':
            return PhoneCallIcon;
        default:
            return ActivityIcon;
    }
}

// Used in list page, so the passed in meeting is the stub
export function getLocationText(meeting: MeetingStubFragment): string {
    switch (meeting.locationType) {
        case 'ADDRESS':
            return meeting.location;
        case 'INBOUND_CALL':
            return 'Inbound call - Your meeting participant will call you';
        case 'OUTBOUND_CALL':
            return 'Outbound call - You call your participant';
        case 'MEET':
        case 'TEAMS':
        case 'ZOOM':
            return 'Links provided for each booking';
        default:
            return '';
    }
}

export function getStatusBadge(
    meeting: MeetingStubFragment | Meeting | MeetingForLandingPage | ManagedMeeting
): StatusBadgeProps {
    if (meeting.deletedTime) {
        return {level: 'hidden', text: 'Archived'};
    }

    if (meeting.validity.isBookable) {
        return {level: 'success', text: 'Accepting bookings'};
    }

    return {level: 'info--grey', text: 'Not accepting bookings'};
}

export function getAdditionalStatusBadges(meeting: MeetingStubFragment): StatusBadgeProps[] {
    const additionalStatusBadges = [];

    const needsAttentionStatusBadge = getNeedsAttentionStatusBadge(meeting);
    if (needsAttentionStatusBadge) {
        additionalStatusBadges.push(needsAttentionStatusBadge);
    }

    const isHiddenStatusBadge = getIsHiddenStatusBadge(meeting);
    if (isHiddenStatusBadge) {
        additionalStatusBadges.push(isHiddenStatusBadge);
    }

    return additionalStatusBadges;
}

export function getNeedsAttentionStatusBadge(
    meeting: MeetingStubFragment
): StatusBadgeProps | null | undefined {
    if (
        meeting.validity.issues.length > 0 ||
        (meeting.meetingType === 'ROUND_ROBIN' && meeting.hosts.length === 0)
    ) {
        return {level: 'alert', text: 'Needs attention'};
    }

    return null;
}

export function getIsHiddenStatusBadge(
    meeting: MeetingStubFragment
): StatusBadgeProps | null | undefined {
    if (meeting.isHidden) {
        return {level: 'hidden', text: 'Hidden'};
    }

    return null;
}

export function getManagedMeetingBadge(
    meeting: MeetingStubFragment
): StatusBadgeProps | null | undefined {
    if (meeting.isManagedMeeting) {
        return {level: 'info', text: 'Managed'};
    }

    return null;
}

export const getTeamName = (
    team:
        | {
              name: string;
          }
        | null
        | undefined,
    hosts: any[]
): string | React.ReactNode => {
    if (!team) {
        return 'Team not found';
    }

    return hosts.length === 0 ? (
        <div
            className='flex align-center gap-4 width-fit-content'
            data-html={true}
            data-delay-show={2}
            data-tip={ReactDOMServer.renderToString(
                <div className='pad-8'>Team currently has zero members</div>
            )}
        >
            {/* @ts-expect-error */}
            <WarningIcon size='12' wrapWithDiv={false} color={colors.error} />
            <Body size='small' color={'error'}>
                {team.name}
            </Body>
        </div>
    ) : (
        team.name
    );
};

export const getHostSectionTitleFromMeetingType = (meeting: Meeting): string | React.ReactNode => {
    switch (meeting.meetingType) {
        case 'SOLO':
            return 'Host';
        case 'COHOSTED':
            return 'Co-hosts';
        case 'ROUND_ROBIN': {
            const teamName = getTeamName(meeting.team, meeting.hosts);

            return (
                <div className='flex gap-8'>
                    Round-robin
                    <Divider variant='line--vertical' />
                    {teamName}
                </div>
            );
        }

        case 'UNKNOWN':
        default:
            return meeting.hosts.length === 1 ? 'Host' : 'Co-hosts';
    }
};

export const getMeetingTypeAsString = (meeting: MeetingStubFragment): string => {
    switch (meeting.meetingType) {
        case 'SOLO':
            return 'Solo';
        case 'COHOSTED':
            return 'Co-host';
        case 'ROUND_ROBIN':
            return 'Round-robin';
        case 'UNKNOWN':
            if (meeting.hosts.length > 1) {
                return 'Co-host';
            } else if (meeting.hosts.length === 1) {
                return 'Solo';
            }

            return '';
        default:
            return '';
    }
};

export function getMeetingUrlInfoText(
    meeting: Meeting,
    customSlugError: string | null | undefined,
    hasFullAccess: boolean,
    user: User
): React.ReactNode | null | undefined {
    if (!hasFullAccess) {
        return (
            <Body size='small' color='light'>
                {user.permissions.canAccessSetup ? (
                    <>
                        Upgrade to{' '}
                        <Link
                            to='/setup/billing/sales'
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                        >
                            Nutshell Business
                        </Link>
                    </>
                ) : (
                    'Ask your administrator to upgrade to Nutshell Business'
                )}{' '}
                to customize your meeting link
            </Body>
        );
    }

    if (meeting.isManagedTemplate) {
        if (meeting.assignments && meeting.assignments.totalCount > 0) {
            return (
                <Body size='small' color='light'>
                    This field cannot be edited once the meeting has been assigned
                </Body>
            );
        }

        return (
            <>
                <Body size='small' color='light'>
                    This will appear as the end of each meeting’s URL if the assigned host has
                    already customized their Scheduler meeting page link.{' '}
                </Body>
                <Link
                    href='https://support.nutshell.com/en/articles/8428989-scheduler-plan-schedule-and-manage-meetings#h_a6f96edc3b'
                    variant='primary'
                    size='small'
                    newTab={true}
                >
                    Learn more
                </Link>
            </>
        );
    }

    if (!meeting.creator.hasCustomizedMeetingUrl) {
        return (
            <Body size='small' color='light'>
                To customize this link,
                {meeting.creator.id === user.id
                    ? ' you’ll need to first personalize your Scheduler meeting page. '
                    : ` ${meeting.creator.name} will need to first personalize their Scheduler meeting page. `}
                <Link
                    href='https://support.nutshell.com/en/articles/8428989-scheduler-plan-schedule-and-manage-meetings#h_a6f96edc3b'
                    variant='primary'
                    size='small'
                    newTab={true}
                >
                    Learn more
                </Link>
            </Body>
        );
    }

    if (customSlugError) {
        return (
            <Body size='small' color='error'>
                {customSlugError}
            </Body>
        );
    }

    return null;
}

export function getFirstConflictCalendarError(
    conflictCalendars: ConflictCalendar[]
): string | null | undefined {
    const firstErrorCalendar = conflictCalendars.find(
        (calendar) => calendar.syncErrors && calendar.syncErrors.length > 0
    );

    return firstErrorCalendar ? firstErrorCalendar.syncErrors[0] : null;
}
